import { auth } from './firebaseUtils';
import { API_BASE_URL } from '../config';
import { devAuthToken } from './devAuth';

let installed = false;

const getApiOrigin = (): string | null => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return null;
  }
};

const shouldAttachAuth = (urlValue: string): boolean => {
  try {
    const resolved = new URL(urlValue, window.location.origin);
    const apiOrigin = getApiOrigin();
    if (apiOrigin && resolved.origin === apiOrigin) {
      return true;
    }
    return (
      resolved.origin === window.location.origin &&
      (resolved.pathname.startsWith('/api/') || resolved.pathname.startsWith('/agent/'))
    );
  } catch {
    return false;
  }
};

export async function getCurrentAuthToken(forceRefresh: boolean = false): Promise<string | null> {
  // There is no Firebase user behind a dev session, so this comes first.
  const devToken = devAuthToken();
  if (devToken) {
    return devToken;
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    return null;
  }

  try {
    return await currentUser.getIdToken(forceRefresh);
  } catch {
    return null;
  }
}

export function installAuthenticatedFetch(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  installed = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const urlValue =
      input instanceof Request
        ? input.url
        : input instanceof URL
          ? input.toString()
          : String(input);

    if (!shouldAttachAuth(urlValue)) {
      return originalFetch(input, init);
    }

    const token = await getCurrentAuthToken();
    if (!token) {
      return originalFetch(input, init);
    }

    const existingHeaders = new Headers(input instanceof Request ? input.headers : init?.headers);
    if (existingHeaders.has('Authorization')) {
      return originalFetch(input, init);
    }

    existingHeaders.set('Authorization', `Bearer ${token}`);

    if (input instanceof Request) {
      const nextRequest = new Request(input, {
        ...init,
        headers: existingHeaders,
      });
      return originalFetch(nextRequest);
    }

    return originalFetch(input, {
      ...init,
      headers: existingHeaders,
    });
  };
}

